import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import RichTextEditor from '../components/RichTextEditor';
import { ArrowLeft, Send, ExternalLink, Plus, Sparkles, Trash2, Loader2, Save, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ['dj', 'venue', 'catering', 'food_truck', 'photography', 'videography', 'florist', 'av_lighting', 'decor', 'transportation', 'entertainment', 'rentals', 'staff', 'other'];

export default function AdminPartnerInvitationEditor() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const confirm = useConfirm();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/partner-invitations/${invitationId}`, { headers });
      setInv(res.data);
    } catch (e) { toast.error('Failed to load'); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId, token]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try {
      const res = await axios.patch(`${API}/partner-invitations/${invitationId}`, patch, { headers });
      setInv(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const setField = (k, v) => setInv(prev => ({ ...prev, [k]: v }));
  const blurSave = (k) => { if (inv) save({ [k]: inv[k] }); };

  // Debounced auto-save — used by RichTextEditor fields (intro_message etc.)
  // since those don't fire onBlur. Schedules a save 1.2s after the last edit.
  const autoSaveTimers = React.useRef({});
  const debouncedSave = (k, v) => {
    setField(k, v);
    if (autoSaveTimers.current[k]) clearTimeout(autoSaveTimers.current[k]);
    autoSaveTimers.current[k] = setTimeout(() => save({ [k]: v }), 1200);
  };

  const aiDraft = async () => {
    if (!aiPrompt.trim()) { toast.error('Describe the listing in a sentence.'); return; }
    setAiLoading(true);
    try {
      await axios.post(`${API}/partner-invitations/${invitationId}/listings/ai-draft`, { prompt: aiPrompt }, { headers });
      toast.success('Listing drafted by AI');
      setAiPrompt('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'AI draft failed'); }
    setAiLoading(false);
  };

  const addManual = async () => {
    await axios.post(`${API}/partner-invitations/${invitationId}/listings`,
      { title: 'New Listing', base_price: 0 }, { headers });
    load();
  };

  const updateListing = async (lid, patch) => {
    await axios.patch(`${API}/partner-invitations/${invitationId}/listings/${lid}`, patch, { headers });
  };

  const removeListing = async (lid) => {
    const ok = await confirm({
      title: 'Remove this listing?',
      body: 'The drafted listing will be deleted from this invitation. This cannot be undone.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/partner-invitations/${invitationId}/listings/${lid}`, { headers });
      toast.success('Listing removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const sendInvite = async () => {
    if (!inv.partner_email) { toast.error('Partner email is required to send'); return; }
    try {
      await axios.post(`${API}/partner-invitations/${invitationId}/send`, {}, { headers });
      toast.success(`Sent to ${inv.partner_email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center">Not found</div>;

  return (
    <div className="min-h-screen bg-background py-10 px-4 sm:px-6 lg:px-8" data-testid="admin-invitation-editor">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate('/admin/partner-invitations')} className="flex items-center text-muted-foreground hover:text-foreground mb-6 text-sm">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <Badge variant="outline" className="mb-2 capitalize">{inv.category} · {inv.status}</Badge>
            <h1 className="text-3xl sm:text-4xl font-bold">{inv.partner_business_name || 'Untitled'}</h1>
            <p className="text-muted-foreground">{inv.partner_contact_name} · {inv.partner_email}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <a href={`/partner-invitation/${inv.token}`} target="_blank" rel="noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" /> Preview
              </a>
            </Button>
            <Button onClick={sendInvite} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="send-btn">
              <Send className="w-4 h-4 mr-2" /> {inv.status === 'draft' ? 'Send Invitation' : 'Resend'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column — Partner details + content */}
          <div className="lg:col-span-2 space-y-4">
            <Card><CardContent className="p-4 space-y-3">
              <h3 className="font-semibold">Partner Details (pre-fill what you know)</h3>
              <div>
                <label className="text-xs text-muted-foreground">Category</label>
                <Select value={inv.category} onValueChange={(v) => save({ category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' / ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Field label="Business Name" v={inv.partner_business_name} onChange={(v) => setField('partner_business_name', v)} onBlur={() => blurSave('partner_business_name')} />
                <Field label="Legal Name" v={inv.partner_legal_name} onChange={(v) => setField('partner_legal_name', v)} onBlur={() => blurSave('partner_legal_name')} />
                <Field label="DBA" v={inv.partner_dba} onChange={(v) => setField('partner_dba', v)} onBlur={() => blurSave('partner_dba')} />
                <Field label="Contact Name" v={inv.partner_contact_name} onChange={(v) => setField('partner_contact_name', v)} onBlur={() => blurSave('partner_contact_name')} />
                <Field label="Email" v={inv.partner_email} onChange={(v) => setField('partner_email', v)} onBlur={() => blurSave('partner_email')} />
                <Field label="Phone" v={inv.partner_phone} onChange={(v) => setField('partner_phone', v)} onBlur={() => blurSave('partner_phone')} />
                <Field label="Address" v={inv.partner_address} onChange={(v) => setField('partner_address', v)} onBlur={() => blurSave('partner_address')} />
                <Field label="City" v={inv.partner_city} onChange={(v) => setField('partner_city', v)} onBlur={() => blurSave('partner_city')} />
                <Field label="State" v={inv.partner_state} onChange={(v) => setField('partner_state', v)} onBlur={() => blurSave('partner_state')} />
                <Field label="ZIP" v={inv.partner_zip} onChange={(v) => setField('partner_zip', v)} onBlur={() => blurSave('partner_zip')} />
                <Field label="Website" v={inv.partner_website} onChange={(v) => setField('partner_website', v)} onBlur={() => blurSave('partner_website')} />
                <Field label="Logo URL (https)" v={inv.partner_logo_url} onChange={(v) => setField('partner_logo_url', v)} onBlur={() => blurSave('partner_logo_url')} />
                <Field label="Cover Image URL (https)" v={inv.partner_cover_url} onChange={(v) => setField('partner_cover_url', v)} onBlur={() => blurSave('partner_cover_url')} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Bio</label>
                <Textarea rows={3} value={inv.partner_bio || ''} onChange={(e) => setField('partner_bio', e.target.value)} onBlur={() => blurSave('partner_bio')} />
              </div>
            </CardContent></Card>

            <Card><CardContent className="p-4 space-y-3">
              <h3 className="font-semibold">Pitch Content (override defaults)</h3>
              <div>
                <label className="text-xs text-muted-foreground">Tagline</label>
                <Input value={inv.tagline || ''} onChange={(e) => setField('tagline', e.target.value)} onBlur={() => blurSave('tagline')} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>Personal intro message (optional)</span>
                  <span className="text-[10px] text-fuchsia-400">auto-saves</span>
                </label>
                <RichTextEditor value={inv.intro_message || ''} onChange={(html) => debouncedSave('intro_message', html)} minHeight={120} />
              </div>
              <BulletEditor label="Why Eventism" items={inv.why_eventism || []} onChange={(arr) => save({ why_eventism: arr })} testid="why-eventism" />
              <BulletEditor label="How it works" items={inv.how_it_works || []} onChange={(arr) => save({ how_it_works: arr })} testid="how-it-works" />
              <div>
                <label className="text-xs text-muted-foreground">Earnings example</label>
                <Textarea rows={2} value={inv.earnings_example || ''} onChange={(e) => setField('earnings_example', e.target.value)} onBlur={() => blurSave('earnings_example')} />
              </div>
              <FaqEditor items={inv.faq || []} onChange={(arr) => save({ faq: arr })} />
            </CardContent></Card>
          </div>

          {/* Right column — Listings */}
          <div className="space-y-4">
            <Card><CardContent className="p-4 space-y-3">
              <h3 className="font-semibold flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-fuchsia-400" /> AI-Assist Listing
              </h3>
              <p className="text-xs text-muted-foreground">Type a sentence — Claude drafts a full listing for you to edit.</p>
              <Textarea rows={2} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                placeholder='e.g. "4-hour wedding DJ with sound + lights, $1500"' data-testid="ai-prompt" />
              <Button onClick={aiDraft} disabled={aiLoading} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="ai-draft-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {aiLoading ? 'Drafting…' : 'AI Draft'}
              </Button>
              <Button onClick={addManual} variant="outline" className="w-full" data-testid="add-manual-listing-btn">
                <Plus className="w-4 h-4 mr-2" /> Add manually
              </Button>
            </CardContent></Card>

            <div className="space-y-3" data-testid="listings-list">
              {(inv.listings || []).length === 0 && (
                <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">No listings yet — AI-draft your first one above.</CardContent></Card>
              )}
              {(inv.listings || []).map((L, idx) => (
                <ListingCard key={L.listing_id} listing={L} onSave={(p) => updateListing(L.listing_id, p)} onRemove={() => removeListing(L.listing_id)} index={idx} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, onBlur }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <Input value={v || ''} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
    </div>
  );
}

function BulletEditor({ label, items, onChange, testid }) {
  const [local, setLocal] = useState(items);
  useEffect(() => setLocal(items), [items]);
  const upd = (i, v) => setLocal(local.map((x, j) => j === i ? v : x));
  const add = () => setLocal([...local, '']);
  const remove = (i) => { const n = local.filter((_, j) => j !== i); setLocal(n); onChange(n); };
  const commit = () => onChange(local.filter(x => (x || '').trim()));
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <label className="text-xs text-muted-foreground">{label}</label>
        <Button size="sm" variant="ghost" onClick={add}><Plus className="w-3 h-3" /></Button>
      </div>
      <div className="space-y-1.5">
        {local.map((it, i) => (
          <div key={i} className="flex gap-1.5">
            <Input value={it} onChange={(e) => upd(i, e.target.value)} onBlur={commit} />
            <Button size="icon" variant="ghost" onClick={() => remove(i)}><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqEditor({ items, onChange }) {
  const [local, setLocal] = useState(items);
  useEffect(() => setLocal(items), [items]);
  const upd = (i, k, v) => setLocal(local.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const add = () => setLocal([...local, { q: '', a: '' }]);
  const remove = (i) => { const n = local.filter((_, j) => j !== i); setLocal(n); onChange(n); };
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs text-muted-foreground">FAQ</label>
        <Button size="sm" variant="ghost" onClick={add}><Plus className="w-3 h-3" /></Button>
      </div>
      <div className="space-y-2">
        {local.map((f, i) => (
          <div key={i} className="p-2 rounded border border-border space-y-1">
            <Input placeholder="Question" value={f.q || ''} onChange={(e) => upd(i, 'q', e.target.value)} onBlur={() => onChange(local)} />
            <Textarea rows={2} placeholder="Answer" value={f.a || ''} onChange={(e) => upd(i, 'a', e.target.value)} onBlur={() => onChange(local)} />
            <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => remove(i)}>Remove</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListingCard({ listing, onSave, onRemove, index }) {
  const [local, setLocal] = useState(listing);
  useEffect(() => setLocal(listing), [listing]);
  const set = (k, v) => setLocal(p => ({ ...p, [k]: v }));
  const blur = (k) => onSave({ [k]: local[k] });

  const updateTier = (i, k, v) => {
    const next = (local.package_tiers || []).map((t, j) => j === i ? { ...t, [k]: v } : t);
    setLocal(p => ({ ...p, package_tiers: next }));
  };

  return (
    <Card data-testid={`listing-card-${index}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex justify-between items-center">
          <Input className="font-semibold text-base" value={local.name || local.title || ''} onChange={(e) => set('name', e.target.value)} onBlur={() => blur('name')} placeholder="Listing name" />
          <Button size="sm" variant="ghost" className="text-destructive" onClick={onRemove}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
        <Input placeholder="Short description (1-line tagline)" value={local.short_description || ''} onChange={(e) => set('short_description', e.target.value)} onBlur={() => blur('short_description')} />
        <Textarea rows={4} value={local.description || ''} onChange={(e) => set('description', e.target.value)} onBlur={() => blur('description')} placeholder="Description…" />
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" placeholder="Base price" value={local.base_price || 0} onChange={(e) => set('base_price', parseFloat(e.target.value) || 0)} onBlur={() => blur('base_price')} />
          <Input placeholder="Pricing unit" value={local.pricing_unit_label || ''} onChange={(e) => set('pricing_unit_label', e.target.value)} onBlur={() => blur('pricing_unit_label')} />
          <Input placeholder="Sub-category (e.g. wedding)" value={local.sub_category || ''} onChange={(e) => set('sub_category', e.target.value)} onBlur={() => blur('sub_category')} />
          <Input placeholder="Service location" value={local.service_location || 'on_site'} onChange={(e) => set('service_location', e.target.value)} onBlur={() => blur('service_location')} />
          <Input type="number" placeholder="Min hours" value={local.min_hours || 0} onChange={(e) => set('min_hours', parseInt(e.target.value, 10) || 0)} onBlur={() => blur('min_hours')} />
          <Input type="number" placeholder="Max hours" value={local.max_hours || 0} onChange={(e) => set('max_hours', parseInt(e.target.value, 10) || 0)} onBlur={() => blur('max_hours')} />
          <Input type="number" placeholder="Min guests" value={local.min_guests || 0} onChange={(e) => set('min_guests', parseInt(e.target.value, 10) || 0)} onBlur={() => blur('min_guests')} />
          <Input type="number" placeholder="Max guests" value={local.max_guests || 0} onChange={(e) => set('max_guests', parseInt(e.target.value, 10) || 0)} onBlur={() => blur('max_guests')} />
        </div>
        <Input placeholder="Image URL" value={local.image_url || ''} onChange={(e) => set('image_url', e.target.value)} onBlur={() => blur('image_url')} />
        {(local.package_tiers || []).length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">Package tiers</div>
            {local.package_tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-3 gap-1.5">
                <Input placeholder="Name" value={t.name || ''} onChange={(e) => updateTier(i, 'name', e.target.value)} onBlur={() => blur('package_tiers')} />
                <Input type="number" placeholder="Price" value={t.price || 0} onChange={(e) => updateTier(i, 'price', parseFloat(e.target.value) || 0)} onBlur={() => blur('package_tiers')} />
                <Input placeholder="Features (comma)" value={Array.isArray(t.features) ? t.features.join(', ') : ''}
                  onChange={(e) => updateTier(i, 'features', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  onBlur={() => blur('package_tiers')} />
              </div>
            ))}
          </div>
        )}
        {(local.whats_included || local.included_items || []).length > 0 && (
          <div className="text-xs text-muted-foreground">
            Included: {(local.whats_included || local.included_items).join(' · ')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
